/* eslint-disable no-restricted-syntax */
import { useState, useEffect } from 'react'
import moment from 'moment'
import { formatDateForApi, DateFilters } from 'pixel-base'
import {
  getDashboardOrders,
  getDashboardProducts,
  getDashboardCollections,
} from 'utils/api-routes-analytics'

export interface DashboardData {
  date: string
  total_orders: number
  total_revenue: number
  avg_order_value: number
  net_orders: number
  cancelled_orders: number
  cancelled_revenue: number
  net_revenue: number
}

export interface DashboardProductsData {
  products: Array<{
    base_product_id: number
    product_name: string
    total_items_sold: number
    total_revenue: number
    collection_names: string[]
    variations: Array<{
      variation_id: number
      sku: string
      product_name: string
      items_sold: number
      revenue: number
      image: string | null
    }>
  }>
  top_collections: Array<{
    collection_name: string
    total_articles_sold: number
    total_revenue: number
    unique_products_count: number
    total_orders: number
  }>
  meta: {
    total: number
    period: {
      start_date: string
      end_date: string
    }
    sort_by: string
    filters: {
      include_deleted?: boolean
      excluded_statuses?: string[]
      include_archived?: boolean
      [key: string]: boolean | string[] | undefined
    }
  }
}

export interface DashboardCollectionsData {
  top_collections: Array<{
    collection_name: string
    total_articles_sold: number
    total_revenue: number
    unique_products_count: number
    total_orders: number
  }>
  meta: {
    total: number
    period: {
      start_date: string
      end_date: string
    }
    sort_by: string
    filters: {
      include_deleted?: boolean
      excluded_statuses?: string[]
      include_archived?: boolean
      [key: string]: boolean | string[] | undefined
    }
  }
}

export interface DashboardFilters extends DateFilters {}

// Type-safe response interfaces
interface OrdersApiResponse {
  data: Array<{
    date?: string
    month?: string
    total_orders?: number
    gross_orders?: number
    landed_orders?: number
    total_revenue?: number
    avg_order_value?: number
    net_orders?: number
    shipped_orders?: number
    cancelled_orders?: number
    cancelled_revenue?: number
    net_revenue?: number
  }>
}

interface ProductsApiResponse {
  data: Array<{
    base_product_id: number
    product_name: string
    total_items_sold: number
    total_revenue: number
    collection_names: string[]
    variations: Array<{
      variation_id: number
      sku: string
      product_name: string
      items_sold: number
      revenue: number
      image: string | null
    }>
  }>
  top_collections: Array<{
    collection_name: string
    total_articles_sold: number
    total_revenue: number
    unique_products_count: number
    total_orders: number
  }>
  meta: {
    total: number
    period: {
      start_date: string
      end_date: string
    }
    sort_by: string
    filters: {
      include_deleted?: boolean
      excluded_statuses?: string[]
      include_archived?: boolean
      [key: string]: boolean | string[] | undefined
    }
  }
}

// Type guard functions
function isOrdersApiResponse(response: unknown): response is OrdersApiResponse {
  return (
    response !== null &&
    typeof response === 'object' &&
    'data' in response &&
    Array.isArray((response as OrdersApiResponse).data)
  )
}

function isProductsApiResponse(
  response: unknown,
): response is ProductsApiResponse {
  return (
    response !== null &&
    typeof response === 'object' &&
    'data' in response &&
    'top_collections' in response &&
    'meta' in response &&
    Array.isArray((response as ProductsApiResponse).data) &&
    Array.isArray((response as ProductsApiResponse).top_collections)
  )
}

export const useDashboardData = (
  filters: DashboardFilters,
  chartType: 'orders' | 'revenue' = 'orders',
) => {
  const [data, setData] = useState<DashboardData[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setLoading(true)

    const subscription = getDashboardOrders({
      start_date: formatDateForApi(filters.start_date),
      end_date: formatDateForApi(filters.end_date),
    }).subscribe({
      next: (res: any) => {
        const response = res.response
        if (response?.data) {
          const transformedData = response.data.map((item: any) => ({
            date:
              (typeof item.date === 'string' ? item.date : '') ||
              (typeof item.month === 'string' ? item.month : '') ||
              '',
            total_orders:
              (typeof item.total_orders === 'number' ? item.total_orders : 0) ||
              (typeof item.gross_orders === 'number' ? item.gross_orders : 0) ||
              (typeof item.landed_orders === 'number'
                ? item.landed_orders
                : 0) ||
              0,
            total_revenue:
              (typeof item.total_revenue === 'number'
                ? item.total_revenue
                : 0) || 0,
            avg_order_value:
              (typeof item.avg_order_value === 'number'
                ? item.avg_order_value
                : 0) || 0,
            net_orders:
              (typeof item.net_orders === 'number' ? item.net_orders : 0) ||
              (typeof item.shipped_orders === 'number'
                ? item.shipped_orders
                : 0) ||
              0,
            cancelled_orders:
              (typeof item.cancelled_orders === 'number'
                ? item.cancelled_orders
                : 0) || 0,
            cancelled_revenue:
              (typeof item.cancelled_revenue === 'number'
                ? item.cancelled_revenue
                : 0) || 0,
            net_revenue:
              (typeof item.net_revenue === 'number' ? item.net_revenue : 0) ||
              0,
          }))
          setData(transformedData)
        } else {
          setData([])
        }
        setLoading(false)
      },
      error: (error: any) => {
        console.error('useDashboardData: Orders API error:', error)
        setData([])
        setLoading(false)
      },
    })

    return () => subscription.unsubscribe()
  }, [filters.start_date, filters.end_date])

  // Transform the response data
  const transformedData = data

  // Ensure data is always an array and sort it safely
  const sortedData = Array.isArray(transformedData)
    ? [...transformedData].sort((a, b) => {
        return new Date(a.date).getTime() - new Date(b.date).getTime()
      })
    : []

  // Extract x-axis dates for charts
  const xAxisDates = sortedData.map((item) => item.date)

  // Calculate totals for display
  const totalOrders = sortedData.reduce(
    (sum, item) => sum + (item.total_orders || 0),
    0,
  )
  const totalRevenue = sortedData.reduce(
    (sum, item) => sum + (item.total_revenue || 0),
    0,
  )

  return {
    data: sortedData,
    loading,
    xAxisDates,
    totalOrders,
    totalRevenue,
  }
}

export const useDashboardProductsData = (filters: DashboardFilters) => {
  const [data, setData] = useState<DashboardProductsData | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setLoading(true)

    const subscription = getDashboardProducts({
      start_date: formatDateForApi(filters.start_date),
      end_date: formatDateForApi(filters.end_date),
    }).subscribe({
      next: (res: any) => {
        const response = res.response
        if (response) {
          const productsData = {
            products: response.data || [],
            top_collections: response.top_collections || [],
            meta: response.meta || {
              total: 0,
              period: { start_date: '', end_date: '' },
              sort_by: '',
              filters: {},
            },
          }
          setData(productsData)
        } else {
          setData(null)
        }
        setLoading(false)
      },
      error: (error: any) => {
        console.error('useDashboardProductsData: Products API error:', error)
        setData(null)
        setLoading(false)
      },
    })

    return () => subscription.unsubscribe()
  }, [filters.start_date, filters.end_date])

  // Transform the response data
  const productsData = data

  return {
    data: productsData,
    loading,
    products: productsData?.products || [],
    topCollections: productsData?.top_collections || [],
    meta: productsData?.meta || {
      total: 0,
      period: { start_date: '', end_date: '' },
      sort_by: '',
      filters: {},
    },
  }
}

export const useDashboardCollectionsData = (filters: DashboardFilters) => {
  const [data, setData] = useState<DashboardCollectionsData | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setLoading(true)

    const subscription = getDashboardCollections({
      start_date: formatDateForApi(filters.start_date),
      end_date: formatDateForApi(filters.end_date),
    }).subscribe({
      next: (res: any) => {
        const response = res.response
        if (response) {
          const collectionsData = {
            top_collections: response.data || [],
            meta: response.meta || {
              total: 0,
              period: { start_date: '', end_date: '' },
              sort_by: '',
              filters: {},
            },
          }
          setData(collectionsData)
        } else {
          setData(null)
        }
        setLoading(false)
      },
      error: (error: any) => {
        console.error(
          'useDashboardCollectionsData: Collections API error:',
          error,
        )
        setData(null)
        setLoading(false)
      },
    })

    return () => subscription.unsubscribe()
  }, [filters.start_date, filters.end_date])

  // Transform the response data
  const collectionsData = data

  return {
    data: collectionsData,
    loading,
    topCollections: collectionsData?.top_collections || [],
    meta: collectionsData?.meta || {
      total: 0,
      period: { start_date: '', end_date: '' },
      sort_by: '',
      filters: {},
    },
  }
}
